import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Send, Users, Bell, Package, FolderTree, UserCircle2, Loader2, Check, AlertTriangle, Eye, MousePointerClick } from "lucide-react";

const SEGMENTS = [
  { key: "all_customers", label: "All customers", icon: Users, needsParam: false, helper: "Every registered customer" },
  { key: "all_sellers", label: "All sellers", icon: Users, needsParam: false, helper: "Every seller account" },
  { key: "all_admins", label: "All admins", icon: Users, needsParam: false, helper: "All admin accounts" },
  { key: "wishlisters_of_product", label: "Wishlisters of a product", icon: Package, needsParam: true, paramKind: "product", helper: "Users who wishlisted a specific product" },
  { key: "wishlisters_of_category", label: "Wishlisters of a category", icon: FolderTree, needsParam: true, paramKind: "category", helper: "Users who wishlisted any product in a category" },
  { key: "creator_followers", label: "Followers of a creator", icon: UserCircle2, needsParam: true, paramKind: "creator", helper: "Users following a specific creator/atelier" },
];

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-[hsl(var(--kb-border))] p-5" data-testid={`push-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-[hsl(var(--kb-muted))] text-xs uppercase tracking-wide mb-1">
        <Icon size={14} /> {label}
      </div>
      <div className="font-display text-3xl">{value}</div>
    </div>
  );
}

export default function AdminPushBroadcast() {
  const [form, setForm] = useState({
    title: "",
    body: "",
    url: "/",
    segment: "all_customers",
    segment_param: "",
  });
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [paramOptions, setParamOptions] = useState({ products: [], categories: [], creators: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const current = useMemo(() => SEGMENTS.find((s) => s.key === form.segment), [form.segment]);

  const loadAll = async () => {
    try {
      const [h, s] = await Promise.all([
        api.get("/admin/push/broadcasts"),
        api.get("/admin/push/stats"),
      ]);
      setHistory(h.data.items || []);
      setStats(s.data);
      setLoadError(null);
    } catch (e) {
      console.warn("admin-push-load", e);
      setLoadError(e?.response?.data?.detail || "Could not load broadcast data");
    }
  };

  const loadParamOptions = async () => {
    try {
      const [products, categories, creators] = await Promise.all([
        api.get("/products?limit=50&sort=popularity").catch(() => ({ data: { items: [] } })),
        api.get("/categories").catch(() => ({ data: [] })),
        api.get("/creators").catch(() => ({ data: [] })),
      ]);
      setParamOptions({
        products: products.data.items || [],
        categories: Array.isArray(categories.data) ? categories.data : (categories.data.items || []),
        creators: Array.isArray(creators.data) ? creators.data : [],
      });
    } catch (e) {
      console.warn("admin-push-options", e);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      await Promise.all([loadAll(), loadParamOptions()]);
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  // Re-run preview whenever the segment (or its param) changes
  useEffect(() => {
    let alive = true;
    const run = async () => {
      if (current?.needsParam && !form.segment_param) {
        setPreview(null);
        return;
      }
      setPreviewing(true);
      try {
        const { data } = await api.get("/admin/push/segment-preview", {
          params: { segment: form.segment, param: form.segment_param || undefined },
        });
        if (alive) setPreview(data);
      } catch (e) {
        if (alive) setPreview({ error: e?.response?.data?.detail || "Could not compute" });
      } finally {
        if (alive) setPreviewing(false);
      }
    };
    run();
    return () => { alive = false; };
  }, [form.segment, form.segment_param, current]);

  const canSend = form.title.trim() && form.body.trim() && (!current?.needsParam || form.segment_param) && preview?.user_count > 0 && !sending;

  const send = async () => {
    setSending(true);
    try {
      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        url: form.url.trim() || "/",
        segment: form.segment,
        segment_param: current?.needsParam ? form.segment_param : null,
      };
      const { data } = await api.post("/admin/push/broadcast", payload);
      toast.success(`Broadcast sent — ${data.sent}/${data.attempted} delivered`);
      setForm((f) => ({ ...f, title: "", body: "" }));
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send broadcast");
    } finally {
      setSending(false);
    }
  };

  const paramInput = () => {
    if (!current?.needsParam) return null;
    const kind = current.paramKind;
    if (kind === "product") {
      return (
        <select
          className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm"
          value={form.segment_param}
          onChange={(e) => setForm({ ...form, segment_param: e.target.value })}
          data-testid="push-param-product"
        >
          <option value="">-- Select a product --</option>
          {paramOptions.products.map((p) => (
            <option key={p.id} value={p.id}>{p.title}</option>
          ))}
        </select>
      );
    }
    if (kind === "category") {
      return (
        <select
          className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm"
          value={form.segment_param}
          onChange={(e) => setForm({ ...form, segment_param: e.target.value })}
          data-testid="push-param-category"
        >
          <option value="">-- Select a category --</option>
          {paramOptions.categories.map((c) => (
            <option key={c.id || c.slug} value={c.id || c.slug}>{c.name || c.title}</option>
          ))}
        </select>
      );
    }
    if (kind === "creator") {
      return (
        <select
          className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm"
          value={form.segment_param}
          onChange={(e) => setForm({ ...form, segment_param: e.target.value })}
          data-testid="push-param-creator"
        >
          <option value="">-- Select a creator --</option>
          {paramOptions.creators.map((c) => (
            <option key={c.id} value={c.id}>{c.display_name || c.handle}</option>
          ))}
        </select>
      );
    }
    return null;
  };

  return (
    <div className="space-y-8" data-testid="admin-push-broadcast">
      <div>
        <h1 className="font-display text-3xl">Push Broadcasts</h1>
        <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">Send a one-off native notification to a targeted audience. Respects each user's per-topic preference and dedupes across their devices.</p>
      </div>

      {loadError && (
        <div
          className="border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm flex items-start gap-2"
          data-testid="push-load-error"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{loadError}</span>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="push-loading-skeleton">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-[92px] bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
          ))}
        </div>
      ) : stats ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Stat label="Total broadcasts" value={stats.total_broadcasts} icon={Send} />
          <Stat label="Total delivered" value={stats.total_sent} icon={Check} />
          <Stat label="Total attempted" value={stats.total_attempted} icon={Users} />
          <Stat label="Total shown" value={stats.total_shown} icon={Eye} />
          <Stat label="Total clicks" value={stats.total_clicks} icon={MousePointerClick} />
          <Stat label="Overall CTR" value={`${stats.overall_ctr}%`} icon={Bell} />
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Composer */}
        <div className="lg:col-span-3 bg-white border border-[hsl(var(--kb-border))] p-6 space-y-4" data-testid="push-composer">
          <h2 className="font-display text-xl">Compose notification</h2>

          <div>
            <label className="text-sm font-medium">Title <span className="text-red-500">*</span></label>
            <input
              className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Mega Monsoon Sale is live"
              maxLength={120}
              data-testid="push-title"
            />
            <div className="text-xs text-[hsl(var(--kb-muted))] mt-1">{form.title.length}/120</div>
          </div>

          <div>
            <label className="text-sm font-medium">Message <span className="text-red-500">*</span></label>
            <textarea
              rows={3}
              className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Up to 50% off on sarees, lehengas and kurtas. Tap to shop."
              maxLength={400}
              data-testid="push-body"
            />
            <div className="text-xs text-[hsl(var(--kb-muted))] mt-1">{form.body.length}/400</div>
          </div>

          <div>
            <label className="text-sm font-medium">Click-through URL</label>
            <input
              className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="/campaign/monsoon"
              data-testid="push-url"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Target audience</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
              {SEGMENTS.map((s) => {
                const Icon = s.icon;
                const active = form.segment === s.key;
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setForm({ ...form, segment: s.key, segment_param: "" })}
                    className={`flex items-start gap-2 border p-3 text-left text-sm transition ${active ? "border-[hsl(var(--kb-primary))] bg-[hsl(var(--kb-primary))]/5" : "border-[hsl(var(--kb-border))] hover:border-[hsl(var(--kb-primary))]/40"}`}
                    data-testid={`push-segment-${s.key}`}
                  >
                    <Icon size={16} className="mt-0.5 shrink-0" />
                    <div>
                      <div className="font-medium">{s.label}</div>
                      <div className="text-xs text-[hsl(var(--kb-muted))]">{s.helper}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            {current?.needsParam && (
              <div className="mt-3">
                <label className="text-sm font-medium">Select {current.paramKind}</label>
                <div className="mt-1">{paramInput()}</div>
              </div>
            )}
          </div>

          {/* Live preview */}
          <div className="border border-dashed border-[hsl(var(--kb-border))] p-3 flex items-center justify-between text-sm" data-testid="push-preview">
            <div className="flex items-center gap-2">
              {previewing ? <Loader2 size={14} className="animate-spin" /> : <Users size={14} />}
              <span>
                {previewing ? "Calculating…" :
                  preview?.error ? <span className="text-red-600">{preview.error}</span> :
                  preview ? <>Will reach <b data-testid="push-preview-count">{preview.user_count}</b> user{preview.user_count === 1 ? "" : "s"}</> :
                  "Select a segment to see reach"}
              </span>
            </div>
            {preview?.user_count === 0 && !previewing && !preview?.error && (
              <span className="flex items-center gap-1 text-amber-700 text-xs">
                <AlertTriangle size={12} /> No one matches — nothing will be sent
              </span>
            )}
          </div>

          <button
            type="button"
            disabled={!canSend}
            onClick={send}
            className="kb-btn-primary inline-flex items-center gap-2"
            data-testid="push-send-btn"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? "Sending…" : "Send broadcast"}
          </button>
        </div>

        {/* History */}
        <div className="lg:col-span-2 bg-white border border-[hsl(var(--kb-border))] p-6" data-testid="push-history">
          <h2 className="font-display text-xl mb-4">Recent broadcasts</h2>
          {history.length === 0 ? (
            <div className="text-sm text-[hsl(var(--kb-muted))]">No broadcasts yet.</div>
          ) : (
            <ul className="space-y-3 max-h-[600px] overflow-y-auto kb-scroll-hide">
              {history.map((b) => (
                <li key={b.id} className="border border-[hsl(var(--kb-border))] p-3 text-sm" data-testid={`push-history-${b.id}`}>
                  <div className="font-medium">{b.title}</div>
                  <div className="text-xs text-[hsl(var(--kb-muted))] mt-0.5 line-clamp-2">{b.body}</div>
                  <div className="flex items-center justify-between mt-2 text-xs gap-2">
                    <span className="bg-[hsl(var(--kb-surface-alt))] px-2 py-0.5 truncate">{b.segment}{b.segment_param ? ` · ${String(b.segment_param).slice(0, 8)}` : ""}</span>
                    <span className="text-[hsl(var(--kb-muted))] shrink-0">{b.sent}/{b.attempted} sent</span>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-[hsl(var(--kb-muted))]">
                    <span className="inline-flex items-center gap-1" data-testid={`push-shown-${b.id}`}>
                      <Eye size={12} /> {b.shown_count || 0} shown
                    </span>
                    <span className="inline-flex items-center gap-1" data-testid={`push-clicks-${b.id}`}>
                      <MousePointerClick size={12} /> {b.click_count || 0} clicks
                    </span>
                    <span className="inline-flex items-center gap-1 text-[hsl(var(--kb-primary))] font-medium" data-testid={`push-ctr-${b.id}`}>
                      {(b.ctr || 0)}% CTR
                    </span>
                  </div>
                  <div className="text-[10px] text-[hsl(var(--kb-muted))] mt-1">{new Date(b.created_at).toLocaleString()}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
